import {createDivIn, createImgIn, createOptionIn, createParagraphIn, createSelectIn, createSpanIn, setElementInnerHTML, setElementOnClick} from '../../ui_utils/DomUtils.ts'
import {LobbyImage} from '@colonist/types'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables.ts'
import {clientI18n} from '../../ClientI18n.ts'
import {type RoomSessionClientState} from '../../RoomSessionClientState.ts'
import {BotDifficulty} from '@colonist/game-base-types'
import {EnumUtils} from '@colonist/utils'
import {PlayerColor} from '@colonist/game-states'
import {getLobbyImagePath} from '../../ui_utils/Utils.ts'
import {ViewPopupChangeOnlineUsername} from '../../ui_popup/ViewPopupChangeOnlineUsername.ts'
import {type TranslationObject} from '@colonist/i18n'
import {UIRoomUserList} from './UIRoomUserList.ts'
import {createTooltipIn, updateTooltipText} from '../../ui_utils/TooltipUtils.ts'
import IconHourglass from '@static/images/icon_hourglass.svg'
import {UIImagePathController} from '../../UIEnums.ts'
import {socket} from '../../EntryShared.ts'
import {AudioManager, AudioType} from '@colonist/audio'

export interface UserActionIcon {
    icon: LobbyImage
    tooltip: TranslationObject
    action?: () => void
}

export class UIRoomUserView {
    private readonly container: HTMLDivElement
    private readonly infoContainer: HTMLDivElement
    private readonly readyContainer: HTMLDivElement

    private usernameAndKarmaContainer: HTMLDivElement
    private readonly nameContainer: HTMLDivElement
    private usernameContainer: HTMLDivElement
    private karmaContainer: HTMLDivElement
    private difficultySelectorContainer: HTMLSelectElement
    private readonly colorContainer: HTMLDivElement

    state?: RoomSessionClientState | undefined

    private profilePic: HTMLImageElement
    private userName: HTMLSpanElement
    private usernameInfoIcon: HTMLDivElement
    private actionIconsContainer: HTMLDivElement

    private karmaText: HTMLSpanElement

    private readonly colorRoad: HTMLImageElement
    private readonly colorSettlement: HTMLImageElement
    private readonly colorCity: HTMLImageElement

    private readinessContainer: HTMLDivElement
    private statusText: HTMLSpanElement
    private addBotButton: HTMLDivElement

    private readonly PLAYER_ICON_SIZE = 28
    private readonly ACTION_ICON_SIZE = 18
    private readonly ICON_SIZE = 17

    private readonly isGuest: boolean

    constructor(container: HTMLDivElement) {
        this.container = createDivIn(container, 'room_player_name')
        this.infoContainer = createDivIn(this.container, 'room_player_info')
        this.readyContainer = createDivIn(this.container, 'room_player_ready_container room_player_not_ready_color')
        this.nameContainer = createDivIn(this.infoContainer, 'room_player_user')
        this.colorContainer = createDivIn(this.infoContainer, 'room_player_color')

        this.renderUserInfo()

        this.colorRoad = UIRoomUserView.createIconIn(this.colorContainer, LobbyImage.RoadBronze, this.ICON_SIZE)
        this.colorSettlement = UIRoomUserView.createIconIn(this.colorContainer, LobbyImage.SettlementBronze, this.ICON_SIZE)
        this.colorCity = UIRoomUserView.createIconIn(this.colorContainer, LobbyImage.CityBronze, this.ICON_SIZE)

        this.renderReadinessState()
        this.createAddBotButton()
        this.removePlayer()
    }

    private renderUserInfo() {
        this.profilePic = UIRoomUserView.createIconIn(this.nameContainer, LobbyImage.Guest, this.PLAYER_ICON_SIZE)
        this.usernameAndKarmaContainer = createDivIn(this.nameContainer, 'room_player_username_and_karma')
        this.usernameContainer = createDivIn(this.usernameAndKarmaContainer, 'room_player_username_container')
        this.userName = createSpanIn(this.usernameContainer, 'room_player_username', {key: 'strings:profilePage.friends.tableHeaders.player'})
        this.renderUsernameInfoIcon()

        this.karmaContainer = createDivIn(this.usernameAndKarmaContainer, 'room_player_karma')
        this.renderKarma()

        this.difficultySelectorContainer = createSelectIn(this.usernameAndKarmaContainer, 'room_player_difficulty_selector hidden', '', () => undefined)
        this.renderBotDifficultySelector()
    }

    private renderUsernameInfoIcon() {
        this.usernameInfoIcon = createDivIn(this.usernameContainer, 'room_player_username_info_icon')
        UIRoomUserView.createIconIn(this.usernameInfoIcon, LobbyImage.InfoIcon, this.ICON_SIZE)
        createTooltipIn(this.usernameInfoIcon, {key: 'strings:roomPage.onlineUsernameTooltip'})

        setElementOnClick(this.usernameInfoIcon, () => {
            new ViewPopupChangeOnlineUsername(() => undefined)
        }, false, true)

        this.hideUsernameInfoIcon()
    }

    private renderKarma() {
        this.karmaText = createParagraphIn(this.karmaContainer, {key: 'strings:colonistCommon.utils.emptyString'}, 'room_player_karma_text')
        createTooltipIn(this.karmaContainer, {key: 'strings:homePage.overview.karma'}, undefined, undefined, 'small')
        setElementOnClick(this.karmaContainer, () => undefined, false, true)
    }

    private renderBotDifficultySelector() {
        EnumUtils.iterateEnum(BotDifficulty, (value: string) => {
            createOptionIn(this.difficultySelectorContainer, value, {key: 'strings:colonistCommon.botDifficulties.difficulty', options: {context: value}})
        })
    }

    private renderReadinessState() {
        this.actionIconsContainer = createDivIn(this.readyContainer, 'room-player-actions-container')
        this.readinessContainer = createDivIn(this.readyContainer, 'room_player_ready')
        this.statusText = createSpanIn(this.readinessContainer, '', {key: 'strings:roomPage.notReady'})
    }

    private createAddBotButton() {
        this.addBotButton = createDivIn(this.container, 'btn_general btn_add_bot', 'add-bot-button')
        createImgIn(this.addBotButton, LobbyImage.Bot)
        createSpanIn(this.addBotButton, '', {key: 'strings:roomPage.addBot'})
        setElementOnClick(this.addBotButton, () => {
            AudioManager.play(AudioType.Click)
            socket.roomClient.addBot()
        })
    }

    removePlayer() {
        this.infoContainer.style.display = 'none'
        this.readyContainer.style.display = 'none'
        this.addBotButton.style.display = 'flex'
        this.state = undefined
        // eslint-disable-next-line no-restricted-syntax
        this.container.onclick = () => undefined
    }

    update(newState: RoomSessionClientState) {
        if(this.colorChanged(newState)) this.updateColor(newState.selectedColor)
        this.updateKarma(newState)
        this.updateBotDifficultySelectorValue(newState.botDifficulty ?? BotDifficulty.BotMedium)
        if(this.statusChanged(newState)) this.updateStatus(newState)
        if(this.statusChanged(newState)) this.updateReadyContainerClass(newState)
        this.state = newState
        this.updateProfilePic()
        this.updateUsernameInfoIcon()
        this.showPlayer()
    }

    private updateProfilePic() {
        if(this.state == undefined) return

        const imgPath = this.state.profilePictureUrl ?? getLobbyImagePath(this.state.icon)
        if(this.profilePic.src != imgPath) this.profilePic.src = imgPath

        this.profilePic.classList.toggle('profile-picture', this.state.profilePictureUrl != undefined)
    }

    updateActionIcons(icons: UserActionIcon[]) {
        setElementInnerHTML(this.actionIconsContainer, {key: 'strings:colonistCommon.utils.emptyString'})
        for(const icon of icons) {
            const imageContainer = createDivIn(this.actionIconsContainer, 'room-player-action-icon-container')
            const img = createImgIn(imageContainer, icon.icon, 'darken-on-hover')
            if(icon.action != undefined) {
                const action = icon.action
                setElementOnClick(img, () => action(), false, true)
            }
            if(icon.tooltip == undefined) continue
            if(UIUtilsEjsVariables.isUserOnMobileAppOrWeb) continue
            createTooltipIn(imageContainer, icon.tooltip)
        }
    }

    updateColor(color: PlayerColor) {
        if(color == PlayerColor.None) {
            this.colorRoad.src = IconHourglass
            this.colorSettlement.src = IconHourglass
            this.colorCity.src = IconHourglass
            return
        }
        this.colorRoad.src = UIImagePathController.roadsByColor[color]
        this.colorSettlement.src = UIImagePathController.settlementsByColor[color]
        this.colorCity.src = UIImagePathController.citiesByColor[color]
    }

    updateKarma(state: RoomSessionClientState) {
        if(state.isBot) {
            this.karmaContainer.style.display = 'none'
            return
        }
        if(state.profilePictureUrl != undefined) {
            this.karmaContainer.style.display = 'none'
            return
        }
        setElementInnerHTML(this.karmaText, {
            key: 'strings:homePage.header.playerKarma',
            options: {
                totalCompleted: state.karmaCompletedGames,
                totalGames: state.karmaTotalGames,
            },
        })
        updateTooltipText(this.karmaContainer, {key: 'strings:roomPage.karmaPlayerTooltip'})
        this.karmaContainer.style.display = 'flex'
    }

    setBotDifficultySelectorVisibility(visible: boolean) {
        if(visible) {
            this.difficultySelectorContainer.classList.remove('hidden')
            this.usernameAndKarmaContainer.classList.add('room_player_username_and_karma_bot')
            return
        }
        this.difficultySelectorContainer.classList.add('hidden')
        this.usernameAndKarmaContainer.classList.remove('room_player_username_and_karma_bot')
    }

    setBotDifficultySelectorDisabled(disabled: boolean) {
        this.difficultySelectorContainer.disabled = disabled

        disabled ? this.difficultySelectorContainer.classList.add('disabled') : this.difficultySelectorContainer.classList.remove('disabled')
    }

    updateBotDifficultySelectorValue(botDifficulty: BotDifficulty) {
        this.difficultySelectorContainer.value = botDifficulty.toString()
    }

    setUsername(username: string) {
        setElementInnerHTML(this.userName, {key: 'strings:colonistCommon.utils.#buffer', options: {value: ` ${username} `}})
    }

    private updateStatus(state: RoomSessionClientState) {
        if(state.isReadyToPlay) {
            this.statusText.innerText = clientI18n.t('strings:roomPage.ready').toLocaleUpperCase()
            this.container.classList.remove('room_player_name_not_ready')
            return
        }
        this.statusText.innerText = clientI18n.t('strings:roomPage.notReady')
        this.container.classList.add('room_player_name_not_ready')
    }

    private updateReadyContainerClass(state: RoomSessionClientState) {
        if(state.isReadyToPlay) this.readyContainer.classList.remove('room_player_not_ready_color')
        else this.readyContainer.classList.add('room_player_not_ready_color')
    }

    private showPlayer() {
        this.infoContainer.style.display = 'flex'
        this.readyContainer.style.display = 'flex'
        this.hideBotButton()
    }

    setColorOnclick(action: () => void) {
        setElementOnClick(this.colorContainer, () => {
            AudioManager.play(AudioType.Click)
            action()
        })
    }

    setBotDifficultySelectorOnClick(action: (botDifficulty: BotDifficulty) => void) {
        this.difficultySelectorContainer.onchange = (e: Event) => {
            const botDifficulty = Number(this.difficultySelectorContainer.value) as BotDifficulty
            AudioManager.play(AudioType.Click)
            action(botDifficulty)
        }
    }

    setOnclick(action: () => void) {
        // eslint-disable-next-line no-restricted-syntax
        this.container.onclick = (e) => {
            if(e.target == this.colorContainer) return
            if(e.target == this.difficultySelectorContainer) return
            if(Array.from(this.difficultySelectorContainer.children).includes(e.target as Element)) return

            if(e.target != this.colorContainer && e.target != this.difficultySelectorContainer) {
                AudioManager.play(AudioType.Click)
                action()
            }
        }
    }

    toggleColorSelector(show: boolean) {
        if(show) {
            this.colorContainer.classList.add('can_edit_color')
            return
        }
        this.colorContainer.classList.remove('can_edit_color')
    }

    hideView() {
        if(this.container.style.display == 'none') return
        this.container.style.display = 'none'
    }

    showView() {
        if(this.container.style.display == 'flex') return
        this.container.style.display = 'flex'
    }

    displayBotButton() {
        if(this.state != undefined) return
        this.addBotButton.style.display = 'flex'
    }

    hideBotButton() {
        this.addBotButton.style.display = 'none'
    }

    showUsernameInfoIcon() {
        this.usernameInfoIcon.style.display = 'inherit'
    }

    hideUsernameInfoIcon() {
        this.usernameInfoIcon.style.display = 'none'
    }

    isShowingPlayer(): boolean {
        return this.infoContainer.style.display == 'flex'
    }

    enableHover() {
        this.container.classList.add('hover-enabled')
    }

    disableHover() {
        this.container.classList.remove('hover-enabled')
    }

    private updateUsernameInfoIcon() {
        this.shouldShowUsernameInfoIcon() ? this.showUsernameInfoIcon() : this.hideUsernameInfoIcon()
    }

    private shouldShowUsernameInfoIcon(): boolean {
        if(!UIUtilsEjsVariables.isUserOnDiscord) return false
        if(this.state?.profilePictureUrl != undefined) return false
        return this.isItMe()
    }

    private colorChanged(newState: RoomSessionClientState): boolean {
        if(this.state == undefined) return true
        return this.state.selectedColor != newState.selectedColor
    }

    private statusChanged(newState: RoomSessionClientState): boolean {
        if(this.state == undefined) return true
        return this.state.isReadyToPlay != newState.isReadyToPlay
    }

    private static createIconIn(container: HTMLElement, icon: LobbyImage, size: number) {
        const img = createImgIn(container, icon)
        img.width = size
        img.height = size
        return img
    }

    private isItMe(): boolean {
        if(this.state == undefined) return false
        return UIRoomUserList.isItMe(this.state)
    }
}
